/**
 * Shared Axios Client with Cache-Busting and Auth Interceptors
 *
 * Features:
 * - Adds timestamp parameter to GET requests to bypass browser cache
 * - Handles 401 errors with automatic token refresh
 * - Triggers session expiration handler when refresh fails
 */

import axios from 'axios';
import { setupInterceptors } from './axiosInterceptor';

// Session expiration handler - set by AuthContext
let sessionExpiredHandler = null;

/**
 * Configure the session expired handler
 * Called by AuthContext to connect logout callback
 * @param {Function} handler - Function to call when session expires
 */
export function configureSessionExpiredHandler(handler) {
  sessionExpiredHandler = handler;
}

/**
 * Create axios client with cache-busting and auth interceptors
 * @param {string} baseURL - Base URL for the API
 * @returns {AxiosInstance} Configured axios instance
 */
export function createApiClient(baseURL) {
  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
    },
  });

  // Add timestamp to GET requests to bypass browser cache
  client.interceptors.request.use(
    (config) => {
      if (config.method === 'get') {
        config.params = {
          ...config.params,
          _t: Date.now(),
        };
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Setup auth interceptors for token refresh and 401 handling
  setupInterceptors(client, {
    onLogout: () => {
      if (sessionExpiredHandler) {
        sessionExpiredHandler();
      }
    }
  });

  return client;
}

export default createApiClient;
